const _excluded = ["Cell"];
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTable, useExpanded, useResizeColumns, useBlockLayout, useRowSelect, useFilters } from 'buzz_lib_react_table';
import { useSticky } from 'react-table-sticky';
import { getTheme, Shimmer, Stack } from 'office-ui-fabric-react';
import { Scrollbars } from 'react-custom-scrollbars';
import { TableWrapper } from './table.style';
import { getMaxHeaderDepth, generateColumnsIfCollapsed } from './table.helper';
import { List } from 'react-virtualized';
import { TableHeader } from './Header';
import { TableRow } from './Row';
import useWindowSize from '../../hooks/useWindowSize';
import { useDeepCompareEffect } from 'use-deep-compare';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const Table = props => {
  var _tableRef$current, _listHeaderRef$curren;
  const [tableColumns, setTableColumns] = useState([]);
  const [refreshColumnsFlag, setRefreshColumnsFlag] = useState(false);
  const tableRef = useRef(null);
  const listHeaderRef = useRef(null);
  const listBodyRef = useRef(null);
  const [scrollLeftPosition, setScrollLeftPosition] = useState(0);
  const windowSize = useWindowSize();
  const availableHeight = (_tableRef$current = tableRef.current) === null || _tableRef$current === void 0 || (_tableRef$current = _tableRef$current.parentElement) === null || _tableRef$current === void 0 ? void 0 : _tableRef$current.clientHeight;
  const headerHeight = (_listHeaderRef$curren = listHeaderRef.current) === null || _listHeaderRef$curren === void 0 ? void 0 : _listHeaderRef$curren.clientHeight;
  const listHeight = useMemo(() => {
    return availableHeight && headerHeight ? availableHeight - headerHeight : 0;
  }, [availableHeight, headerHeight, windowSize.height, windowSize.width]);
  useEffect(() => {
    setTableColumns(props.columns);
  }, [props.columns]);
  useEffect(() => {
    scrollHeaderHorizontal(0);
  }, [props.isLoading]);
  const maxHeaderDepth = useMemo(() => getMaxHeaderDepth(props.columns), []);
  const defaultColumn = useMemo(() => ({
    minWidth: 50,
    maxWidth: 400
  }), []);
  const addUncollapsableColumn = useCallback((column, currentDepth) => {
    const newColumn = _objectSpread(_objectSpread({}, column), {}, {
      hasCustomCell: column.Cell ? true : false,
      columns: generateColumns(column.columns, currentDepth + 1)
    });
    return newColumn;
  }, []);
  const addCollapsableColumn = useCallback((column, currentDepth) => {
    const {
        Cell
      } = column,
      restCol = _objectWithoutProperties(column, _excluded);
    const newColumn = _objectSpread(_objectSpread({}, restCol), {}, {
      hasCustomCell: Cell ? true : false,
      columns: column.isExpanded ? generateColumns(column.columns, currentDepth + 1) : [generateColumnsIfCollapsed(column, maxHeaderDepth - currentDepth, Cell)]
    });
    return newColumn;
  }, [maxHeaderDepth]);
  const generateColumns = useCallback(function (columns) {
    let currentDepth = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    const newColumns = [];
    for (let i = 0; i < columns.length; i++) {
      var _column$columns;
      const column = columns[i];
      if (!column.columns || !((_column$columns = column.columns) !== null && _column$columns !== void 0 && _column$columns.length)) {
        newColumns.push(_objectSpread(_objectSpread({}, column), {}, {
          hasCustomCell: column.Cell ? true : false
        }));
      } else if (!column.isCollapsable) {
        const newColumn = addUncollapsableColumn(column, currentDepth);
        newColumns.push(newColumn);
      } else {
        const newColumn = addCollapsableColumn(column, currentDepth);
        newColumns.push(newColumn);
      }
    }
    return newColumns;
  }, []);
  const columns = useMemo(() => {
    return generateColumns(tableColumns);
  }, [tableColumns, refreshColumnsFlag, generateColumns]);
  useEffect(() => {
    scrollHeaderHorizontal(0);
    scrollTable({
      target: {
        scrollTop: 0,
        scrollLeft: 0
      }
    });
  }, [props.reinitScrollFlag]);
  useEffect(() => {
    scrollHeaderHorizontal(scrollLeftPosition);
  }, [tableColumns]);
  useEffect(() => {
    scrollHeaderHorizontal(0);
  }, [props.data.length]);
  const {
    state,
    headerGroups,
    rows,
    getTableProps,
    getTableBodyProps,
    prepareRow,
    totalColumnsWidth
  } = useTable(_objectSpread(_objectSpread({
    columns,
    data: props.data,
    initialState: props.initialState,
    defaultColumn,
    updateData: props.updateData,
    autoResetSelectedRows: false
  }, props.settings), {}, {
    autoResetPage: false,
    autoResetExpanded: false,
    autoResetFilters: false
  }), useExpanded, useBlockLayout, useResizeColumns, useFilters, useSticky, useRowSelect);
  useDeepCompareEffect(() => {
    props.getState && props.getState(state);
  }, [state]);
  const scrollHeaderHorizontal = useCallback(scrollLeft => {
    if (!listHeaderRef.current) {
      return;
    }
    const headerRows = listHeaderRef.current.children;
    for (let i = 0; i < headerRows.length; i++) {
      for (let j = 0; j < headerRows[i].children.length; j++) {
        const child = headerRows[i].children[j];
        if (child.innerHTML.indexOf('data-sticky-td') === -1) {
          child.style.left = "".concat(String(-scrollLeft), "px");
        }
      }
    }
  }, [listHeaderRef.current]);
  useEffect(() => {
    scrollTable({
      target: {
        scrollTop: 0,
        scrollLeft: scrollLeftPosition
      }
    });
  }, [props.forceScrollTopFlag]);
  const scrollTable = useCallback(_ref => {
    var _listBodyRef$current;
    let {
      target
    } = _ref;
    setScrollLeftPosition(target.scrollLeft);
    if (!listHeaderRef.current || !((_listBodyRef$current = listBodyRef.current) !== null && _listBodyRef$current !== void 0 && _listBodyRef$current.Grid)) {
      return;
    }
    const scrollTop = target.scrollTop;
    const scrollLeft = target.scrollLeft;
    listBodyRef.current.Grid.handleScrollEvent({
      scrollLeft,
      scrollTop
    });
    scrollHeaderHorizontal(scrollLeft);
  }, [listHeaderRef.current, scrollHeaderHorizontal]);
  useEffect(() => {
    setRefreshColumnsFlag(!refreshColumnsFlag);
  }, [props.forceUpdateFlag]);
  const refreshColumns = useCallback(() => {
    if (!props.shouldColumnsRefresh) {
      return;
    }
    setRefreshColumnsFlag(current => !current);
    if (props.onColumnsRefresh) {
      props.onColumnsRefresh();
    }
  }, [props.onColumnsRefresh, props.shouldColumnsRefresh, setRefreshColumnsFlag]);
  const {
    TableRowComponent,
    TableWrapperComponent
  } = props;
  const hasData = useMemo(() => {
    if (!props.hasDataFunction) {
      return true;
    }
    return props.hasDataFunction(rows);
  }, [rows]);
  const ListShimmer = useMemo(() => {
    const shimmerRowHeight = 15;
    const shimmerRowVerticalGap = 14;
    const shimmerRowWithMarginHeight = shimmerRowHeight + shimmerRowVerticalGap;
    const numberOfRows = Math.floor(listHeight / shimmerRowWithMarginHeight);
    const rows = numberOfRows > 0 ? new Array(numberOfRows).fill(' ') : [];
    return /*#__PURE__*/_jsx(Stack, {
      styles: {
        root: {
          height: listHeight,
          padding: 2,
          paddingTop: shimmerRowVerticalGap
        }
      },
      "data-id": "table-grid-loader",
      children: rows.map((_element, index) => /*#__PURE__*/_jsx(Shimmer, {
        styles: {
          root: {
            height: shimmerRowWithMarginHeight,
            opacity: 0.1 * (rows.length - index)
          }
        },
        ariaLabel: "Loading content"
      }, index))
    });
  }, [listHeight, tableColumns]);
  const renderScrollTrackHorizontal = useCallback(_ref2 => {
    let {
      style,
      props: trackProps
    } = _ref2;
    const trackStyle = {
      height: props.scrollThumbWidth,
      right: 2,
      bottom: 2,
      left: 2,
      borderRadius: 3
    };
    return /*#__PURE__*/_jsx("div", _objectSpread({
      style: _objectSpread(_objectSpread({}, style), trackStyle)
    }, trackProps));
  }, []);
  const renderScrollTrackVertical = useCallback(_ref3 => {
    let {
      style,
      props: trackProps
    } = _ref3;
    const trackStyle = {
      width: props.scrollThumbWidth,
      right: 2,
      bottom: 2,
      top: 2,
      borderRadius: 3
    };
    return /*#__PURE__*/_jsx("div", _objectSpread({
      style: _objectSpread(_objectSpread({}, style), trackStyle)
    }, trackProps));
  }, []);
  const changeTableColumns = useCallback(newTableColumns => {
    setTableColumns(newTableColumns);
    setRefreshColumnsFlag(curr => !curr);
  }, [setRefreshColumnsFlag]);
  if (!TableWrapperComponent) {
    return null;
  }
  return /*#__PURE__*/_jsx(TableWrapperComponent, {
    ref: tableRef,
    theme: getTheme(),
    children: /*#__PURE__*/_jsxs("div", _objectSpread(_objectSpread({}, getTableProps()), {}, {
      className: "table sticky",
      style: {
        overflow: 'hidden'
      },
      children: [/*#__PURE__*/_jsx(TableHeader, {
        headerGroups: headerGroups,
        componentRef: listHeaderRef,
        sortBy: props.sortBy,
        onSortChange: props.onSortChange,
        tableColumns: tableColumns,
        changeTableColumns: changeTableColumns,
        hasData: hasData,
        isDataLoading: props.isLoading
      }), /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({}, getTableBodyProps()), {}, {
        className: "body",
        children: props.isLoading ? ListShimmer : !props.data.length && props.noDataMessage ? /*#__PURE__*/_jsx(Stack, {
          tokens: {
            childrenGap: 5
          },
          styles: {
            root: {
              height: listHeight,
              padding: 5,
              flexDirection: 'row',
              justifyContent: 'center',
              color: '#494847',
              fontWeight: 500
            }
          },
          children: props.noDataMessage
        }) : /*#__PURE__*/_jsx("div", {
          style: {
            height: listHeight,
            position: 'relative'
          },
          children: /*#__PURE__*/_jsx(Scrollbars, {
            autoHeight: false,
            onScrollStop: refreshColumns,
            onScroll: scrollTable,
            renderTrackHorizontal: props.scrollThumbWidth ? renderScrollTrackHorizontal : undefined,
            renderTrackVertical: props.scrollThumbWidth ? renderScrollTrackVertical : undefined,
            children: /*#__PURE__*/_jsx(List, {
              overscanColumnCount: 0,
              overscanRowCount: 0,
              className: "body-list",
              width: totalColumnsWidth,
              height: listHeight,
              rowCount: rows.length,
              rowHeight: 55,
              ref: listBodyRef,
              rowRenderer: rowRendererProps => {
                if (!TableRowComponent) {
                  console.error('No TableRowComponent passed.');
                  return;
                }
                const row = rows[rowRendererProps.index];
                prepareRow(row);
                return /*#__PURE__*/_jsx(TableRowComponent, {
                  row: row,
                  style: rowRendererProps.style
                }, rowRendererProps.key);
              },
              containerStyle: {
                overflow: 'unset'
              }
            })
          })
        })
      }))]
    }))
  });
};
Table.defaultProps = {
  TableWrapperComponent: TableWrapper,
  TableRowComponent: TableRow,
  noDataMessage: 'No records found',
  scrollThumbWidth: 10
};